import { useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { toast } from "sonner";
import { QRCodeSVG } from "qrcode.react";
import {
  AlertTriangle, Check, Clock, CreditCard, ExternalLink, FilePlus2, FolderOpen, Package, Printer, Save, Scissors, Sparkles, Trash2,
} from "lucide-react";
import PageHeader, { ghostBtn } from "@/app/PageHeader";
import { useSession } from "@/app/session";

// Flyers: pick a template, edit the copy, print or save as PDF from the browser. Every flyer is a
// US Letter sheet laid out in real inches, so what you see in the preview is what prints.
// Saved designs live at /api/flyers (backend/flyers.py).

const NAVY = "#050814";
const NAVY_2 = "#0A0E1A";
const INK = "#0B1F4D"; // logo navy, for text on white
const CYAN = "#5CB3E2";
const CYAN_DARK = "#2C86BA"; // cyan with enough contrast on white
const LIGHT = "#F8FAFC";
const SLATE = "#94A3B8";
const HEAD = "Outfit, ui-sans-serif, sans-serif";
const BODY = '"DM Sans", ui-sans-serif, sans-serif';
const TAGLINE = "Modern Vending for a Modern World";

const PHOTOS = [
  { key: "hero", label: "Cooler on navy", src: "/images/smart-cooler-hero.jpg", pos: "50% 45%" },
  { key: "about", label: "Full cooler", src: "/images/smart-cooler-about.jpg", pos: "50% 55%" },
  { key: "interior", label: "Stocked shelves", src: "/images/smart-cooler-interior.jpg", pos: "50% 40%" },
  { key: "lounge", label: "Cooler in lounge", src: "/images/smart-cooler-lounge.jpg", pos: "50% 50%" },
];
const photoOf = (key) => PHOTOS.find((p) => p.key === key) || PHOTOS[0];

const TEMPLATES = [
  { key: "bold", label: "Bold", hint: "Dark brand flyer with hero photo" },
  { key: "clean", label: "Clean", hint: "Light flyer for offices and medical" },
  { key: "half", label: "Half-page", hint: "Two leave-behinds per sheet, cut line" },
  { key: "apartment", label: "Apartment amenity", hint: "For property managers and residents" },
];

// Default copy per audience. Dan's rules: no "free"/no-cost claims, no testimonials, ratings or statistics.
const PRESETS = {
  general: {
    label: "General",
    eyebrow: "Smart cooler vending",
    headline: "Modern vending for a *modern world.*",
    subheadline: "A glass-door smart cooler with cashless tap-to-pay, installed, stocked and serviced by a local team from West Chester, PA.",
    bullets: ["Cold drinks and snacks in one glass-door cooler", "Tap to pay with a card, phone or watch",
      "Always stocked, always clean", "Fast, direct communication with a local owner"],
    cta: "Scan to request a smart cooler",
    note: "We install, stock and service the cooler. Nothing for your staff to manage.",
  },
  office: {
    label: "Office",
    eyebrow: "Breakroom smart cooler",
    headline: "A better breakroom, *handled for you.*",
    subheadline: "We install, stock and service a modern smart cooler, so your team has cold drinks and snacks without anyone running to the store.",
    bullets: ["Soda, water, sports drinks, chips, candy and healthier snacks", "Cashless tap-to-pay with card, phone or watch",
      "Always stocked, always clean", "One local contact who answers fast"],
    cta: "Scan to set up a quick walkthrough",
    note: "We install, stock and service the cooler. Nothing for your office manager to manage.",
  },
  medical: {
    label: "Medical",
    eyebrow: "Smart cooler for medical offices",
    headline: "Refreshments for staff *and waiting families.*",
    subheadline: "A clean, quiet smart cooler that keeps drinks and snacks on hand through long shifts and long waits, installed and serviced by Vortex.",
    bullets: ["Healthier options alongside familiar favorites", "Contactless tap-to-pay checkout",
      "Kept clean and restocked on a regular schedule", "Quick response whenever you need us"],
    cta: "Scan to talk about your practice",
    note: "We install, stock and service the cooler. Nothing for your staff to manage.",
  },
  apartments: {
    label: "Apartments",
    eyebrow: "Resident amenity",
    headline: "An amenity residents *will actually use.*",
    subheadline: "Cold drinks and snacks steps from home. Vortex installs, stocks and services a modern smart cooler in your clubhouse, lobby or fitness room.",
    bullets: ["Open whenever your amenity space is", "Cashless tap-to-pay, no coins or bills",
      "Drinks, snacks and healthier picks", "Always stocked, always clean"],
    cta: "Scan to add a smart cooler to your community",
    note: "We install, stock and service the cooler. Nothing for your leasing or maintenance team to manage.",
  },
  gym: {
    label: "Gym",
    eyebrow: "Smart cooler for fitness centers",
    headline: "Hydration and fuel, *right on the floor.*",
    subheadline: "Water, sports drinks and better-for-you snacks in a modern smart cooler, installed, stocked and serviced by Vortex.",
    bullets: ["Water, sports drinks and better-for-you snacks", "Quick tap-to-pay checkout between sets",
      "Always stocked, always clean", "Local service with fast replies"],
    cta: "Scan to bring a smart cooler to your gym",
    note: "We install, stock and service the cooler. Nothing for your front desk to manage.",
  },
  industrial: {
    label: "Industrial / Warehouse",
    eyebrow: "Smart cooler for every shift",
    headline: "Keep every shift *fueled.*",
    subheadline: "Cold drinks and snacks on site, so crews don't have to leave the building. Vortex installs, stocks and services the cooler.",
    bullets: ["Available to every shift, early or late", "Fast cashless tap-to-pay checkout",
      "Restocked around your schedule", "A local owner who answers the phone"],
    cta: "Scan to schedule a site visit",
    note: "We install, stock and service the cooler. Nothing for your team to manage.",
  },
};
const COPY_KEYS = ["eyebrow", "headline", "subheadline", "bullets", "cta", "note"];

const presetCopy = (audience) => {
  const p = PRESETS[audience] || PRESETS.general;
  return { eyebrow: p.eyebrow, headline: p.headline, subheadline: p.subheadline, bullets: [...p.bullets], cta: p.cta, note: p.note };
};

const defaultFields = (audience = "office") => ({
  audience,
  ...presetCopy(audience),
  photo: "hero",
  contactName: "",
  phone: "(610) 983-8060",
  email: "info@vortexvs.com",
  website: "www.vortexvs.com",
  qrUrl: "",
});

const slugify = (s) => String(s || "").toLowerCase().replace(/[^a-z0-9]+/g, "-").replace(/^-+|-+$/g, "").slice(0, 60);
const autoQr = (template, audience) =>
  `https://www.vortexvs.com/contact?utm_source=flyer&utm_campaign=${slugify(`${template}-${audience}`) || "flyer"}`;

// True while the copy still matches its audience preset (so switching template or audience can safely replace it).
const isPristine = (f) => {
  const p = presetCopy(f.audience);
  return COPY_KEYS.every((k) => JSON.stringify(f[k]) === JSON.stringify(p[k]));
};

// Copy-rule check (Dan's rules) over every text field.
const RULES = [
  [/\bfree\b/i, '"free"'], [/\bno[\s-]?cost\b/i, '"no cost"'], [/\bcomplimentary\b/i, '"complimentary"'],
  [/\bno[\s-]?charge\b/i, '"no charge"'], [/\bat no\b/i, '"at no ..."'], [/\d+(\.\d+)?\s?%/, "a percentage statistic"],
  [/★|\b\d(\.\d)?\s?stars?\b|\brated\b|\breviews?\b/i, "a rating"], [/\btestimonial|["“][^"”]{12,}["”]\s*[-—–]/i, "a testimonial"],
];
const copyWarnings = (f) => {
  const text = [f.eyebrow, f.headline, f.subheadline, ...(f.bullets || []), f.cta, f.note].join(" \n ");
  return RULES.filter(([rx]) => rx.test(text)).map(([, label]) => label);
};

// Pick a font size from the text length: steps = [[maxChars, pt], ...], last entry is the floor.
const fit = (text, steps) => {
  const n = String(text || "").replace(/\*/g, "").length;
  for (const [max, pt] of steps) if (n <= max) return `${pt}pt`;
  return `${steps[steps.length - 1][1]}pt`;
};

// "*accent*" in a headline renders in the accent color.
const Rich = ({ text, accent }) =>
  String(text || "").split(/(\*[^*]+\*)/g).map((part, i) =>
    part.startsWith("*") && part.endsWith("*") && part.length > 2
      ? <span key={i} style={{ color: accent }}>{part.slice(1, -1)}</span>
      : <span key={i}>{part.replace(/\*/g, "")}</span>);

// ---------- flyer building blocks (all sized in inches/points so they print true) ----------

const LogoLight = ({ h }) => (
  <img src="/brand/logo-light.png" alt="Vortex Vending Services" style={{ height: `${h}in`, width: "auto", display: "block" }} />
);

// logo-original.png is the navy logo on a large white canvas; crop to the wordmark (approx. 520..1480 x 465..735 of 2000x1200).
const LogoDark = ({ w }) => {
  const k = w / 960;
  return (
    <div style={{ width: `${w}in`, height: `${270 * k}in`, overflow: "hidden", position: "relative" }}>
      <img src="/brand/logo-original.png" alt="Vortex Vending Services"
        style={{ position: "absolute", width: `${2000 * k}in`, height: `${1200 * k}in`, left: `${-520 * k}in`, top: `${-465 * k}in`, maxWidth: "none" }} />
    </div>
  );
};

const Qr = ({ value, size, fg = NAVY, pad = 0.1, radius = 0.12, caption, captionColor = SLATE }) => (
  <div style={{ display: "flex", flexDirection: "column", alignItems: "center", gap: "0.08in" }}>
    <div style={{ background: "#fff", padding: `${pad}in`, borderRadius: `${radius}in`, lineHeight: 0 }}>
      <QRCodeSVG value={value || "https://www.vortexvs.com"} size={512} level="M" marginSize={0} fgColor={fg} bgColor="#FFFFFF"
        style={{ width: `${size}in`, height: `${size}in`, display: "block" }} />
    </div>
    {caption && <div style={{ fontFamily: BODY, fontSize: "7.5pt", letterSpacing: "0.18em", textTransform: "uppercase", color: captionColor, fontWeight: 500 }}>{caption}</div>}
  </div>
);

const Photo = ({ photo, style }) => {
  const p = photoOf(photo);
  return <img src={p.src} alt="" style={{ objectFit: "cover", objectPosition: p.pos, display: "block", ...style }} />;
};

const CheckDot = ({ bg, fg, size = 0.24 }) => (
  <span style={{ flex: "none", width: `${size}in`, height: `${size}in`, borderRadius: "50%", background: bg, color: fg,
    display: "inline-flex", alignItems: "center", justifyContent: "center", marginTop: "0.01in" }}>
    <Check strokeWidth={3.2} style={{ width: `${size * 0.6}in`, height: `${size * 0.6}in` }} />
  </span>
);

const bulletsOf = (f, max = 4) => (f.bullets || []).map((b) => (b || "").trim()).filter(Boolean).slice(0, max);

const Contact = ({ f, color, strong, size = 11, phoneSize = 17 }) => (
  <div style={{ fontFamily: BODY, color, fontSize: `${size}pt`, lineHeight: 1.45 }}>
    {f.contactName && <div style={{ fontWeight: 500 }}>Ask for {f.contactName}</div>}
    {f.phone && <div style={{ fontFamily: HEAD, fontWeight: 700, fontSize: `${phoneSize}pt`, color: strong, letterSpacing: "0.01em", lineHeight: 1.25 }}>{f.phone}</div>}
    {f.email && <div>{f.email}</div>}
    {f.website && <div>{f.website}</div>}
  </div>
);

const sheet = (bg, color) => ({
  width: "8.5in", height: "11in", position: "relative", overflow: "hidden", background: bg, color,
  fontFamily: BODY, boxSizing: "border-box",
});

// (a) Bold: dark navy, tall photo on the right, footer band with QR.
const BoldFlyer = ({ f, qr }) => {
  const bullets = bulletsOf(f);
  return (
    <div className="vf-sheet" style={sheet(NAVY, LIGHT)}>
      <div style={{ position: "absolute", right: 0, top: 0, width: "3.7in", height: "8.25in" }}>
        <Photo photo={f.photo} style={{ width: "100%", height: "100%" }} />
        <div style={{ position: "absolute", inset: 0, background: `linear-gradient(90deg, ${NAVY} 0%, rgba(5,8,20,0.55) 22%, rgba(5,8,20,0) 50%), linear-gradient(0deg, ${NAVY} 0%, rgba(5,8,20,0) 25%)` }} />
      </div>
      <div style={{ position: "absolute", left: 0, top: 0, width: "6in", height: "8.25in",
        background: `radial-gradient(ellipse at 0% 0%, rgba(92,179,226,0.16), rgba(92,179,226,0) 60%)` }} />
      <div style={{ position: "absolute", left: "0.6in", top: "0.6in", width: "4.85in" }}>
        <LogoLight h={0.62} />
        {f.eyebrow && <div style={{ marginTop: "0.95in", fontSize: "10pt", fontWeight: 500, letterSpacing: "0.24em", textTransform: "uppercase", color: CYAN }}>{f.eyebrow}</div>}
        <div style={{ marginTop: f.eyebrow ? "0.14in" : "0.95in", fontFamily: HEAD, fontWeight: 900, lineHeight: 1.02, letterSpacing: "-0.015em",
          fontSize: fit(f.headline, [[22, 50], [34, 46], [48, 40], [70, 34], [999, 28]]) }}>
          <Rich text={f.headline} accent={CYAN} />
        </div>
        <div style={{ width: "0.7in", height: "4pt", background: CYAN, borderRadius: "2pt", margin: "0.28in 0 0.24in" }} />
        {f.subheadline && <div style={{ fontSize: fit(f.subheadline, [[140, 13.5], [200, 12], [999, 11]]), lineHeight: 1.45, color: "#CBD5E1", maxWidth: "4.5in" }}>{f.subheadline}</div>}
        <div style={{ marginTop: "0.34in", display: "flex", flexDirection: "column", gap: "0.15in" }}>
          {bullets.map((b, i) => (
            <div key={i} style={{ display: "flex", gap: "0.14in", alignItems: "flex-start", fontSize: "12.5pt", lineHeight: 1.35, color: LIGHT }}>
              <CheckDot bg={CYAN} fg={NAVY} /> <span>{b}</span>
            </div>
          ))}
        </div>
      </div>
      <div style={{ position: "absolute", left: 0, right: 0, bottom: 0, height: "2.75in", background: NAVY_2, borderTop: `3pt solid ${CYAN}` }}>
        <div style={{ position: "absolute", left: "0.6in", top: "0.36in", width: "5.2in" }}>
          <div style={{ fontFamily: HEAD, fontWeight: 700, fontSize: fit(f.cta, [[34, 21], [50, 18], [999, 15]]), lineHeight: 1.15, color: "#fff" }}>{f.cta}</div>
          <div style={{ marginTop: "0.16in" }}><Contact f={f} color={SLATE} strong={CYAN} size={11} phoneSize={18} /></div>
        </div>
        <div style={{ position: "absolute", right: "0.6in", top: "0.3in" }}>
          <Qr value={qr} size={1.45} caption="Scan me" />
        </div>
        <div style={{ position: "absolute", left: "0.6in", right: "0.6in", bottom: "0.22in", display: "flex", justifyContent: "space-between",
          fontSize: "7.5pt", letterSpacing: "0.2em", textTransform: "uppercase", color: "#64748B" }}>
          <span>{TAGLINE}</span><span>Vortex Vending Services LLC · West Chester, PA</span>
        </div>
      </div>
    </div>
  );
};

// (b) Clean: white, navy type, photo + bullets, light contact panel.
const CleanFlyer = ({ f, qr }) => {
  const bullets = bulletsOf(f);
  return (
    <div className="vf-sheet" style={sheet("#FFFFFF", INK)}>
      <div style={{ position: "absolute", left: 0, right: 0, top: 0, height: "0.14in", background: `linear-gradient(90deg, ${INK}, ${CYAN})` }} />
      <div style={{ position: "absolute", left: "0.65in", right: "0.65in", top: "0.55in", display: "flex", alignItems: "center", justifyContent: "space-between" }}>
        <LogoDark w={2.35} />
        <div style={{ textAlign: "right", fontSize: "8pt", letterSpacing: "0.2em", textTransform: "uppercase", color: "#64748B", lineHeight: 1.6 }}>
          Smart cooler vending<br />West Chester, PA
        </div>
      </div>
      <div style={{ position: "absolute", left: "0.65in", right: "0.65in", top: "1.55in" }}>
        <div style={{ height: "1pt", background: "#E2E8F0" }} />
        {f.eyebrow && <div style={{ marginTop: "0.32in", fontSize: "10pt", fontWeight: 500, letterSpacing: "0.22em", textTransform: "uppercase", color: CYAN_DARK }}>{f.eyebrow}</div>}
        <div style={{ marginTop: "0.12in", fontFamily: HEAD, fontWeight: 700, lineHeight: 1.06, letterSpacing: "-0.015em",
          fontSize: fit(f.headline, [[26, 44], [44, 38], [64, 32], [999, 27]]) }}>
          <Rich text={f.headline} accent={CYAN_DARK} />
        </div>
        {f.subheadline && <div style={{ marginTop: "0.2in", fontSize: fit(f.subheadline, [[140, 13], [200, 12], [999, 11]]), lineHeight: 1.5, color: "#475569", maxWidth: "6.4in" }}>{f.subheadline}</div>}
      </div>
      <div style={{ position: "absolute", left: "0.65in", right: "0.65in", top: "4.6in", height: "3.35in", display: "flex", gap: "0.4in" }}>
        <div style={{ width: "2.9in", height: "100%", borderRadius: "0.18in", overflow: "hidden", background: NAVY, flex: "none" }}>
          <Photo photo={f.photo} style={{ width: "100%", height: "100%" }} />
        </div>
        <div style={{ flex: 1, display: "flex", flexDirection: "column", justifyContent: "center", gap: "0.2in" }}>
          {bullets.map((b, i) => (
            <div key={i} style={{ display: "flex", gap: "0.15in", alignItems: "flex-start", fontSize: "12.5pt", lineHeight: 1.38, color: INK }}>
              <CheckDot bg="#E0F2FE" fg={CYAN_DARK} size={0.26} /> <span>{b}</span>
            </div>
          ))}
        </div>
      </div>
      <div style={{ position: "absolute", left: "0.65in", right: "0.65in", top: "8.25in", height: "1.95in", background: "#F1F5F9", borderRadius: "0.2in",
        display: "flex", alignItems: "center", justifyContent: "space-between", padding: "0 0.3in 0 0.4in" }}>
        <div style={{ width: "4.6in" }}>
          <div style={{ fontFamily: HEAD, fontWeight: 700, fontSize: fit(f.cta, [[34, 19], [50, 16.5], [999, 14]]), lineHeight: 1.15, color: INK }}>{f.cta}</div>
          <div style={{ marginTop: "0.1in" }}><Contact f={f} color="#475569" strong={INK} size={10.5} phoneSize={16} /></div>
        </div>
        <Qr value={qr} size={1.3} fg={INK} pad={0.08} caption="Scan me" captionColor="#64748B" />
      </div>
      <div style={{ position: "absolute", left: 0, right: 0, bottom: 0, height: "0.5in", background: INK, color: "#E2E8F0",
        display: "flex", alignItems: "center", justifyContent: "space-between", padding: "0 0.65in",
        fontSize: "8pt", letterSpacing: "0.2em", textTransform: "uppercase" }}>
        <span>{TAGLINE}</span><span style={{ color: CYAN }}>{f.website}</span>
      </div>
    </div>
  );
};

// (c) Half-page leave-behind: one 8.5 x 5.5 in card, printed twice with a cut line.
const HalfCard = ({ f, qr }) => {
  const bullets = bulletsOf(f, 3);
  return (
    <div style={{ position: "relative", width: "8.5in", height: "5.5in", overflow: "hidden", background: "#fff", color: INK }}>
      <div style={{ position: "absolute", left: 0, top: 0, width: "2.45in", height: "5.5in", background: NAVY }}>
        <Photo photo={f.photo} style={{ width: "100%", height: "100%" }} />
        <div style={{ position: "absolute", inset: 0, background: `linear-gradient(0deg, rgba(5,8,20,0.85) 0%, rgba(5,8,20,0) 35%)` }} />
        <div style={{ position: "absolute", left: "0.3in", right: "0.2in", bottom: "0.42in", color: "#fff", fontSize: "7.5pt", letterSpacing: "0.2em", textTransform: "uppercase", lineHeight: 1.6 }}>
          {TAGLINE}
        </div>
      </div>
      <div style={{ position: "absolute", left: "2.85in", right: "0.5in", top: "0.45in" }}>
        <LogoDark w={1.75} />
        {f.eyebrow && <div style={{ marginTop: "0.24in", fontSize: "8pt", fontWeight: 500, letterSpacing: "0.22em", textTransform: "uppercase", color: CYAN_DARK }}>{f.eyebrow}</div>}
        <div style={{ marginTop: "0.08in", fontFamily: HEAD, fontWeight: 700, lineHeight: 1.06, letterSpacing: "-0.01em",
          fontSize: fit(f.headline, [[26, 27], [44, 23], [64, 19], [999, 16]]) }}>
          <Rich text={f.headline} accent={CYAN_DARK} />
        </div>
        {f.subheadline && <div style={{ marginTop: "0.1in", fontSize: fit(f.subheadline, [[140, 10], [999, 9]]), lineHeight: 1.45, color: "#475569" }}>{f.subheadline}</div>}
        <div style={{ marginTop: "0.14in", display: "flex", flexDirection: "column", gap: "0.08in" }}>
          {bullets.map((b, i) => (
            <div key={i} style={{ display: "flex", gap: "0.1in", alignItems: "flex-start", fontSize: "10.5pt", lineHeight: 1.3 }}>
              <CheckDot bg="#E0F2FE" fg={CYAN_DARK} size={0.19} /> <span>{b}</span>
            </div>
          ))}
        </div>
      </div>
      <div style={{ position: "absolute", left: "2.85in", right: "0.5in", bottom: "0.42in", display: "flex", alignItems: "flex-end", justifyContent: "space-between", gap: "0.25in" }}>
        <div style={{ minWidth: 0 }}>
          <div style={{ fontFamily: HEAD, fontWeight: 700, fontSize: fit(f.cta, [[34, 12.5], [999, 11]]), lineHeight: 1.2, marginBottom: "0.06in", maxWidth: "3in" }}>{f.cta}</div>
          <Contact f={f} color="#475569" strong={INK} size={9.5} phoneSize={14} />
        </div>
        <Qr value={qr} size={1.15} fg={INK} pad={0.06} radius={0.08} />
      </div>
    </div>
  );
};

const HalfFlyer = ({ f, qr }) => (
  <div className="vf-sheet" style={sheet("#FFFFFF", INK)}>
    <HalfCard f={f} qr={qr} />
    <div style={{ position: "absolute", top: "5.5in", left: 0 }}><HalfCard f={f} qr={qr} /></div>
    <div style={{ position: "absolute", top: "5.5in", left: 0, right: 0, borderTop: "1pt dashed #94A3B8" }} />
    <div style={{ position: "absolute", top: "5.41in", right: "0.3in", background: "#fff", padding: "0 0.06in", display: "flex", alignItems: "center", gap: "0.05in",
      fontSize: "6.5pt", color: "#94A3B8", letterSpacing: "0.18em", lineHeight: 1 }}>
      <Scissors style={{ width: "0.14in", height: "0.14in" }} /> CUT
    </div>
  </div>
);

// (d) Apartment amenity: navy header with photo, amenity tiles, property-manager callout.
const TILE_ICONS = [Clock, CreditCard, Package, Sparkles];
const ApartmentFlyer = ({ f, qr }) => {
  const bullets = bulletsOf(f);
  return (
    <div className="vf-sheet" style={sheet("#FFFFFF", INK)}>
      <div style={{ position: "absolute", left: 0, right: 0, top: 0, height: "4.85in", background: NAVY_2,
        backgroundImage: `radial-gradient(ellipse at 15% 0%, rgba(92,179,226,0.22), rgba(92,179,226,0) 55%)` }} />
      <div style={{ position: "absolute", left: "0.6in", top: "0.55in" }}><LogoLight h={0.55} /></div>
      {f.eyebrow && (
        <div style={{ position: "absolute", right: "0.6in", top: "0.66in", border: `1pt solid ${CYAN}`, color: CYAN, borderRadius: "999px",
          padding: "0.05in 0.16in", fontSize: "8pt", fontWeight: 500, letterSpacing: "0.22em", textTransform: "uppercase" }}>{f.eyebrow}</div>
      )}
      <div style={{ position: "absolute", left: "0.6in", top: "1.65in", width: "4.45in", color: "#fff" }}>
        <div style={{ fontFamily: HEAD, fontWeight: 900, lineHeight: 1.03, letterSpacing: "-0.015em", fontSize: fit(f.headline, [[26, 40], [44, 35], [64, 29], [999, 24]]) }}>
          <Rich text={f.headline} accent={CYAN} />
        </div>
        {f.subheadline && <div style={{ marginTop: "0.2in", fontSize: fit(f.subheadline, [[150, 11.5], [999, 10.5]]), lineHeight: 1.5, color: "#CBD5E1" }}>{f.subheadline}</div>}
      </div>
      <div style={{ position: "absolute", right: "0.6in", top: "1.35in", width: "2.65in", height: "4.25in", borderRadius: "0.2in", overflow: "hidden",
        background: NAVY, boxShadow: "0 0.12in 0.3in rgba(5,8,20,0.35)", border: "3pt solid #fff" }}>
        <Photo photo={f.photo} style={{ width: "100%", height: "100%" }} />
      </div>
      <div style={{ position: "absolute", left: "0.6in", top: "5.15in", fontSize: "9pt", fontWeight: 500, letterSpacing: "0.22em", textTransform: "uppercase", color: CYAN_DARK }}>
        What residents get
      </div>
      <div style={{ position: "absolute", left: "0.6in", right: "0.6in", top: "5.95in", display: "grid", gridTemplateColumns: "1fr 1fr", gap: "0.18in" }}>
        {bullets.map((b, i) => {
          const Icon = TILE_ICONS[i % TILE_ICONS.length];
          return (
            <div key={i} style={{ background: "#F1F5F9", borderRadius: "0.14in", padding: "0.18in 0.2in", display: "flex", gap: "0.16in", alignItems: "center", minHeight: "0.95in" }}>
              <span style={{ flex: "none", width: "0.5in", height: "0.5in", borderRadius: "0.12in", background: INK, color: CYAN, display: "inline-flex", alignItems: "center", justifyContent: "center" }}>
                <Icon strokeWidth={2.2} style={{ width: "0.26in", height: "0.26in" }} />
              </span>
              <span style={{ fontSize: "12pt", lineHeight: 1.3, fontWeight: 500 }}>{b}</span>
            </div>
          );
        })}
      </div>
      <div style={{ position: "absolute", left: "0.6in", right: "0.6in", top: "8.3in", height: "1.95in", borderRadius: "0.18in", border: "1pt solid #E2E8F0",
        display: "flex", alignItems: "center", justifyContent: "space-between", padding: "0 0.28in 0 0.32in", gap: "0.3in" }}>
        <div style={{ flex: 1, minWidth: 0, borderLeft: `4pt solid ${CYAN}`, paddingLeft: "0.22in" }}>
          <div style={{ fontSize: "8pt", fontWeight: 500, letterSpacing: "0.22em", textTransform: "uppercase", color: "#64748B" }}>For property managers</div>
          {f.note && <div style={{ marginTop: "0.05in", fontSize: "10.5pt", lineHeight: 1.4, color: "#334155" }}>{f.note}</div>}
          <div style={{ marginTop: "0.1in", fontFamily: HEAD, fontWeight: 700, fontSize: fit(f.cta, [[34, 15], [50, 13.5], [999, 12]]), lineHeight: 1.15 }}>{f.cta}</div>
          <div style={{ marginTop: "0.04in", fontSize: "10pt", color: "#475569" }}>
            {[f.contactName && `Ask for ${f.contactName}`, f.phone, f.email].filter(Boolean).join("  ·  ")}
          </div>
        </div>
        <Qr value={qr} size={1.3} fg={INK} pad={0.06} />
      </div>
      <div style={{ position: "absolute", left: "0.6in", right: "0.6in", bottom: "0.32in", display: "flex", justifyContent: "space-between",
        fontSize: "7.5pt", letterSpacing: "0.2em", textTransform: "uppercase", color: "#94A3B8" }}>
        <span>Vortex Vending Services LLC · West Chester, PA</span><span>{f.website}</span>
      </div>
    </div>
  );
};

const RENDERERS = { bold: BoldFlyer, clean: CleanFlyer, half: HalfFlyer, apartment: ApartmentFlyer };

const Flyer = ({ template, f }) => {
  const R = RENDERERS[template] || BoldFlyer;
  return <R f={f} qr={(f.qrUrl || "").trim() || autoQr(template, f.audience)} />;
};

// Letter sheet is 816 x 1056 CSS px (96 per inch); scale it to the box width.
const SHEET_W = 816;
const SHEET_H = 1056;
const Scaled = ({ width, children }) => {
  const s = width / SHEET_W;
  return (
    <div style={{ width, height: SHEET_H * s, overflow: "hidden", position: "relative" }}>
      <div style={{ width: SHEET_W, height: SHEET_H, transform: `scale(${s})`, transformOrigin: "0 0", position: "absolute", left: 0, top: 0 }}>{children}</div>
    </div>
  );
};

const PRINT_CSS = `
#vf-print-root { display: none; }
.vf-sheet, .vf-sheet * { box-sizing: border-box; -webkit-print-color-adjust: exact; print-color-adjust: exact; }
@media print {
  @page { size: letter portrait; margin: 0; }
  html, body { margin: 0 !important; padding: 0 !important; background: #fff !important; height: auto !important; overflow: visible !important; }
  body > *:not(#vf-print-root) { display: none !important; }
  #vf-print-root { display: block !important; width: 8.5in; height: 11in; overflow: hidden; }
}
`;

const field = "w-full rounded-md border border-white/10 bg-vortex-ink/60 px-3 py-2 text-sm text-slate-100 placeholder:text-slate-600 focus:border-vortex-cyan/60 focus:outline-none focus:ring-1 focus:ring-vortex-cyan/40";
const label = "mb-1.5 block font-mono text-[10px] tracking-[0.22em] text-slate-400 uppercase";
const section = "border border-white/10 bg-vortex-surface/60 p-5";

const waitForAssets = async (root) => {
  const imgs = root ? [...root.querySelectorAll("img")] : [];
  await Promise.all(imgs.map((img) => (img.complete ? null : new Promise((res) => { img.onload = res; img.onerror = res; }))));
  try { await document.fonts?.ready; } catch { /* fonts API unavailable */ }
};

const fmtDate = (iso) => { try { return new Date(iso).toLocaleDateString(undefined, { month: "short", day: "numeric", year: "numeric" }); } catch { return ""; } };

const Flyers = () => {
  const { api, onAuthError } = useSession();
  const [template, setTemplate] = useState("bold");
  const [f, setF] = useState(() => defaultFields("office"));
  const [name, setName] = useState("");
  const [currentId, setCurrentId] = useState(null);
  const [saved, setSaved] = useState([]);
  const [saving, setSaving] = useState(false);
  const [previewW, setPreviewW] = useState(560);
  const previewRef = useRef(null);
  const printRef = useRef(null);

  const set = (k, v) => setF((prev) => ({ ...prev, [k]: v }));
  const setBullet = (i, v) => setF((prev) => { const b = [...(prev.bullets || [])]; while (b.length < 4) b.push(""); b[i] = v; return { ...prev, bullets: b }; });

  const load = useCallback(async () => {
    try {
      const r = await api.get("/flyers");
      setSaved(r.data.flyers || []);
    } catch (err) { onAuthError(err, "Couldn't load saved flyers."); }
  }, [api, onAuthError]);
  useEffect(() => { load(); }, [load]);

  useLayoutEffect(() => {
    const el = previewRef.current;
    if (!el) return undefined;
    const measure = () => setPreviewW(Math.max(240, Math.min(el.clientWidth, 720)));
    measure();
    const ro = new ResizeObserver(measure);
    ro.observe(el);
    return () => ro.disconnect();
  }, []);

  const applyAudience = (audience) => setF((prev) => ({ ...prev, audience, ...presetCopy(audience), qrUrl: prev.qrUrl }));

  const pickTemplate = (key) => {
    setTemplate(key);
    // The apartment layout reads best with apartment copy; switch only if the copy is still a stock preset.
    if (key === "apartment" && f.audience !== "apartments" && isPristine(f)) applyAudience("apartments");
  };

  const warnings = useMemo(() => copyWarnings(f), [f]);
  const qrAuto = autoQr(template, f.audience);

  const print = async () => {
    await waitForAssets(printRef.current);
    const prevTitle = document.title;
    document.title = (name || `Vortex flyer - ${TEMPLATES.find((t) => t.key === template)?.label}`).replace(/[\\/:*?"<>|]/g, "");
    const restore = () => { document.title = prevTitle; window.removeEventListener("afterprint", restore); };
    window.addEventListener("afterprint", restore);
    window.print();
  };

  const save = async (asNew = false) => {
    const n = name.trim() || `${PRESETS[f.audience]?.label || "Flyer"} - ${TEMPLATES.find((t) => t.key === template)?.label}`;
    setSaving(true);
    try {
      const body = { name: n, template, fields: f };
      const r = currentId && !asNew ? await api.patch(`/flyers/${currentId}`, body) : await api.post("/flyers", body);
      setCurrentId(r.data.id);
      setName(r.data.name);
      toast.success(currentId && !asNew ? "Design updated." : "Design saved.");
      load();
    } catch (err) { onAuthError(err, "Couldn't save the design."); }
    setSaving(false);
  };

  const open = (d) => {
    setTemplate(RENDERERS[d.template] ? d.template : "bold");
    setF({ ...defaultFields(d.fields?.audience || "general"), ...(d.fields || {}) });
    setName(d.name);
    setCurrentId(d.id);
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  const remove = async (d) => {
    if (!window.confirm(`Delete "${d.name}"?`)) return;
    try {
      await api.delete(`/flyers/${d.id}`);
      if (d.id === currentId) setCurrentId(null);
      setSaved((s) => s.filter((x) => x.id !== d.id));
      toast.success("Design deleted.");
    } catch (err) { onAuthError(err, "Couldn't delete the design."); }
  };

  const startNew = () => { setCurrentId(null); setName(""); setTemplate("bold"); setF(defaultFields("office")); };

  const bullets = [...(f.bullets || [])];
  while (bullets.length < 4) bullets.push("");

  return (
    <div data-testid="flyers-page">
      <style>{PRINT_CSS}</style>
      <PageHeader title="Flyers" testId="flyers-heading"
        intro="Pick a template, choose the audience, edit the copy, then print or save as PDF. Everything renders in your browser, so there's no per-flyer cost."
        actions={<>
          <button onClick={startNew} className={ghostBtn} data-testid="flyers-new"><FilePlus2 size={14} /> New</button>
          <button onClick={() => save(false)} disabled={saving} className={ghostBtn} data-testid="flyers-save"><Save size={14} /> {currentId ? "Save changes" : "Save design"}</button>
          {currentId && <button onClick={() => save(true)} disabled={saving} className={ghostBtn} data-testid="flyers-save-new"><Save size={14} /> Save as new</button>}
          <button onClick={print} data-testid="flyers-print"
            className="flex items-center gap-2 rounded-full bg-vortex-cyan px-5 py-2 font-mono text-[10px] tracking-[0.2em] text-vortex-ink uppercase transition-opacity hover:opacity-90">
            <Printer size={14} /> Print / Save as PDF
          </button>
        </>} />

      <div className="grid grid-cols-1 gap-6 xl:grid-cols-[minmax(340px,420px)_1fr]">
        <div className="space-y-5" data-testid="flyers-editor">
          <div className={section}>
            <span className={label}>Template</span>
            <div className="grid grid-cols-4 gap-2">
              {TEMPLATES.map((t) => (
                <button key={t.key} onClick={() => pickTemplate(t.key)} title={t.hint} data-testid={`flyers-template-${t.key}`}
                  className={`group rounded-md border p-1.5 text-left transition-colors ${template === t.key ? "border-vortex-cyan bg-vortex-cyan/10" : "border-white/10 hover:border-white/30"}`}>
                  <div className="pointer-events-none overflow-hidden rounded-sm bg-white/5" aria-hidden>
                    <Scaled width={78}><Flyer template={t.key} f={f} /></Scaled>
                  </div>
                  <p className={`mt-1.5 truncate text-[11px] ${template === t.key ? "text-vortex-cyan" : "text-slate-300"}`}>{t.label}</p>
                </button>
              ))}
            </div>
            <p className="mt-2 text-xs text-slate-500">{TEMPLATES.find((t) => t.key === template)?.hint}. US Letter, 8.5 × 11 in.</p>
          </div>

          <div className={section}>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className={label} htmlFor="fl-audience">Audience preset</label>
                <select id="fl-audience" value={f.audience} onChange={(e) => applyAudience(e.target.value)} className={field} data-testid="flyers-audience">
                  {Object.entries(PRESETS).map(([k, p]) => <option key={k} value={k}>{p.label}</option>)}
                </select>
              </div>
              <div>
                <label className={label} htmlFor="fl-name">Design name</label>
                <input id="fl-name" value={name} onChange={(e) => setName(e.target.value)} placeholder="e.g. Exton office parks" className={field} data-testid="flyers-name" />
              </div>
            </div>
            <p className="mt-2 text-xs text-slate-500">Changing the audience replaces the headline, bullets and call to action with that audience's copy.</p>
          </div>

          <div className={`${section} space-y-4`}>
            <div>
              <label className={label} htmlFor="fl-eyebrow">Small label above headline</label>
              <input id="fl-eyebrow" value={f.eyebrow || ""} onChange={(e) => set("eyebrow", e.target.value)} className={field} maxLength={60} />
            </div>
            <div>
              <label className={label} htmlFor="fl-headline">Headline</label>
              <textarea id="fl-headline" rows={2} value={f.headline || ""} onChange={(e) => set("headline", e.target.value)} className={field} maxLength={110} data-testid="flyers-headline" />
              <p className="mt-1 text-[11px] text-slate-500">Wrap words in *asterisks* to color them cyan.</p>
            </div>
            <div>
              <label className={label} htmlFor="fl-sub">Subheadline</label>
              <textarea id="fl-sub" rows={3} value={f.subheadline || ""} onChange={(e) => set("subheadline", e.target.value)} className={field} maxLength={260} />
            </div>
            <div>
              <span className={label}>Bullet points {template === "half" ? "(first 3 used)" : "(3–4)"}</span>
              <div className="space-y-2">
                {bullets.slice(0, 4).map((b, i) => (
                  <input key={i} value={b} onChange={(e) => setBullet(i, e.target.value)} placeholder={i === 3 ? "Optional 4th point" : `Point ${i + 1}`}
                    aria-label={`Bullet ${i + 1}`} className={field} maxLength={80} />
                ))}
              </div>
            </div>
            <div>
              <label className={label} htmlFor="fl-cta">Call to action</label>
              <input id="fl-cta" value={f.cta || ""} onChange={(e) => set("cta", e.target.value)} className={field} maxLength={70} />
            </div>
            {template === "apartment" && (
              <div>
                <label className={label} htmlFor="fl-note">Property-manager note</label>
                <textarea id="fl-note" rows={2} value={f.note || ""} onChange={(e) => set("note", e.target.value)} className={field} maxLength={160} />
              </div>
            )}
            {warnings.length > 0 && (
              <div className="flex gap-2 rounded-md border border-amber-400/30 bg-amber-400/10 p-3 text-xs text-amber-200" data-testid="flyers-copy-warning">
                <AlertTriangle size={14} className="mt-0.5 shrink-0" />
                <p>Copy rules: the text mentions {warnings.join(", ")}. Vortex flyers avoid "free" or no-cost claims, testimonials, ratings and statistics.</p>
              </div>
            )}
          </div>

          <div className={section}>
            <span className={label}>Photo</span>
            <div className="grid grid-cols-4 gap-2">
              {PHOTOS.map((p) => (
                <button key={p.key} onClick={() => set("photo", p.key)} title={p.label} data-testid={`flyers-photo-${p.key}`}
                  className={`overflow-hidden rounded-md border-2 transition-colors ${f.photo === p.key ? "border-vortex-cyan" : "border-transparent hover:border-white/30"}`}>
                  <img src={p.src} alt={p.label} className="h-20 w-full object-cover" style={{ objectPosition: p.pos }} />
                </button>
              ))}
            </div>
            <p className="mt-2 text-xs text-slate-500">{photoOf(f.photo).label}</p>
          </div>

          <div className={`${section} space-y-4`}>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className={label} htmlFor="fl-contact">Contact name</label>
                <input id="fl-contact" value={f.contactName || ""} onChange={(e) => set("contactName", e.target.value)} placeholder="Optional" className={field} maxLength={60} />
              </div>
              <div>
                <label className={label} htmlFor="fl-phone">Phone</label>
                <input id="fl-phone" value={f.phone || ""} onChange={(e) => set("phone", e.target.value)} className={field} maxLength={30} />
              </div>
              <div>
                <label className={label} htmlFor="fl-email">Email</label>
                <input id="fl-email" value={f.email || ""} onChange={(e) => set("email", e.target.value)} className={field} maxLength={80} />
              </div>
              <div>
                <label className={label} htmlFor="fl-web">Website</label>
                <input id="fl-web" value={f.website || ""} onChange={(e) => set("website", e.target.value)} className={field} maxLength={80} />
              </div>
            </div>
            <div>
              <label className={label} htmlFor="fl-qr">QR code link</label>
              <input id="fl-qr" value={f.qrUrl || ""} onChange={(e) => set("qrUrl", e.target.value)} placeholder={qrAuto} className={field} maxLength={300} data-testid="flyers-qr" />
              <p className="mt-1 flex flex-wrap items-center gap-x-2 text-[11px] text-slate-500">
                <span>{f.qrUrl ? "Custom link." : "Blank uses the contact page with flyer tracking tags."}</span>
                <a href={(f.qrUrl || "").trim() || qrAuto} target="_blank" rel="noreferrer" className="inline-flex items-center gap-1 text-vortex-cyan hover:underline">
                  Test link <ExternalLink size={11} />
                </a>
              </p>
            </div>
          </div>

          <div className={section} data-testid="flyers-saved">
            <span className={label}>Saved designs</span>
            {saved.length === 0 ? (
              <p className="text-sm text-slate-500">Nothing saved yet. Use Save design to keep a flyer for later.</p>
            ) : (
              <ul className="divide-y divide-white/5">
                {saved.map((d) => (
                  <li key={d.id} className="flex items-center gap-3 py-2.5">
                    <button onClick={() => open(d)} className="min-w-0 flex-1 text-left" data-testid={`flyers-open-${d.id}`}>
                      <p className={`truncate text-sm ${d.id === currentId ? "text-vortex-cyan" : "text-slate-100"}`}>{d.name}</p>
                      <p className="truncate text-xs text-slate-500">
                        {TEMPLATES.find((t) => t.key === d.template)?.label || d.template} · {PRESETS[d.fields?.audience]?.label || "General"} · {fmtDate(d.updated_at)}
                      </p>
                    </button>
                    <button onClick={() => open(d)} className="rounded-md p-2 text-slate-400 hover:bg-white/5 hover:text-vortex-cyan" aria-label={`Open ${d.name}`}><FolderOpen size={15} /></button>
                    <button onClick={() => remove(d)} className="rounded-md p-2 text-slate-400 hover:bg-red-500/10 hover:text-red-300" aria-label={`Delete ${d.name}`}><Trash2 size={15} /></button>
                  </li>
                ))}
              </ul>
            )}
          </div>
        </div>

        <div className="min-w-0">
          <div className="xl:sticky xl:top-24">
            <div ref={previewRef} className="mx-auto w-full max-w-[720px]" data-testid="flyers-preview">
              <div className="mx-auto shadow-2xl shadow-black/50 ring-1 ring-white/10" style={{ width: previewW }}>
                <Scaled width={previewW}><Flyer template={template} f={f} /></Scaled>
              </div>
            </div>
            <p className="mt-3 text-center text-xs text-slate-500">
              In the print dialog choose Letter, margins None, and turn on Background graphics. Pick "Save as PDF" as the printer to get a PDF.
            </p>
          </div>
        </div>
      </div>

      {createPortal(<div id="vf-print-root" ref={printRef}><Flyer template={template} f={f} /></div>, document.body)}
    </div>
  );
};

export default Flyers;
